import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketsApi, techniciansApi, aiApi, formatApiError } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  ArrowLeft, Clock, CheckCircle, Activity, User, Phone, Calendar, Bot, Send, Loader2,
  AlertTriangle, Trash2, MessageCircle, Mail, UserCheck, RefreshCw, Bell, MessageSquare
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const chatEndRef = useRef(null);

  useEffect(() => { fetchData(); }, [id]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [ticket?.messages]);

  const fetchData = async () => {
    try {
      const [ticketRes, techRes] = await Promise.all([
        ticketsApi.getById(id),
        techniciansApi.getAll()
      ]);
      setTicket(ticketRes.data);
      setTechnicians(techRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      await ticketsApi.update(id, { status: newStatus });
      fetchData();
    } catch (error) {
      console.error('Status update error:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAssign = async (techId) => {
    try {
      await ticketsApi.update(id, { assigned_to: techId, status: 'in_progress' });
      setShowAssignModal(false);
      fetchData();
    } catch (error) {
      console.error('Assign error:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!chatMessage.trim()) return;
    setSendingMessage(true);
    try {
      await ticketsApi.sendMessage(id, { text: chatMessage.trim(), send_email: sendEmail });
      setChatMessage('');
      fetchData();
    } catch (error) {
      console.error('Send message error:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo ticket?')) return;
    try {
      await ticketsApi.delete(id);
      navigate('/tickets');
    } catch (error) {
      alert(formatApiError(error));
    }
  };

  const handleAiChat = async () => {
    if (!aiMessage.trim()) return;
    setAiLoading(true);
    try {
      const response = await aiApi.chat(aiMessage, id, ticket?.description);
      setAiResponse(response.data.response);
      setAiMessage('');
    } catch (error) {
      setAiResponse('Errore nella comunicazione con REBECCA.');
    } finally {
      setAiLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      urgent: 'bg-red-100 text-[#DC2626] border-[#DC2626]',
      high: 'bg-amber-100 text-[#D97706] border-[#D97706]',
      medium: 'bg-blue-100 text-[#002FA7] border-[#002FA7]',
      low: 'bg-green-100 text-[#16A34A] border-[#16A34A]'
    };
    return styles[priority] || styles.medium;
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { bg: 'bg-blue-100 text-[#002FA7]', icon: <Clock className="w-3 h-3" /> },
      in_progress: { bg: 'bg-amber-100 text-[#D97706]', icon: <Activity className="w-3 h-3" /> },
      resolved: { bg: 'bg-green-100 text-[#16A34A]', icon: <CheckCircle className="w-3 h-3" /> },
      closed: { bg: 'bg-gray-100 text-[#71717A]', icon: <CheckCircle className="w-3 h-3" /> }
    };
    return styles[status] || styles.open;
  };

  const priorityLabels = { urgent: 'URGENTE', high: 'Alta', medium: 'Media', low: 'Bassa' };

  const buildWhatsAppMessage = (phoneNumber) => {
    if (!ticket) return '';
    const pLabel = priorityLabels[ticket.priority] || ticket.priority;
    let msg = `*NUOVO TICKET ASSEGNATO*\n\n*Titolo:* ${ticket.title}\n*ID:* #${ticket.id?.slice(-8)}\n*Priorità:* ${pLabel}\n*Categoria:* ${ticket.category}\n*Descrizione:*\n${ticket.description?.slice(0, 300)}`;
    if (ticket.caller_name || ticket.caller_phone || ticket.caller_email) {
      msg += `\n\n*--- Dati Richiedente ---*`;
      if (ticket.caller_name) msg += `\n*Nome:* ${ticket.caller_name}`;
      if (ticket.caller_phone) msg += `\n*Tel:* ${ticket.caller_phone}`;
      if (ticket.caller_email) msg += `\n*Email:* ${ticket.caller_email}`;
    }
    const cleanPhone = phoneNumber.replace(/\s+/g, '').replace(/^(\+)/, '');
    return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
  };

  const getActivityIcon = (action) => {
    switch(action) {
      case 'created': return <div className="w-7 h-7 rounded-full bg-[#002FA7] flex items-center justify-center"><Clock className="w-3.5 h-3.5 text-white" /></div>;
      case 'assigned': return <div className="w-7 h-7 rounded-full bg-[#16A34A] flex items-center justify-center"><UserCheck className="w-3.5 h-3.5 text-white" /></div>;
      case 'reassigned': return <div className="w-7 h-7 rounded-full bg-[#D97706] flex items-center justify-center"><RefreshCw className="w-3.5 h-3.5 text-white" /></div>;
      case 'status_changed': return <div className="w-7 h-7 rounded-full bg-[#7C3AED] flex items-center justify-center"><Activity className="w-3.5 h-3.5 text-white" /></div>;
      case 'email_sent': return <div className="w-7 h-7 rounded-full bg-[#0EA5E9] flex items-center justify-center"><Mail className="w-3.5 h-3.5 text-white" /></div>;
      case 'whatsapp_sent': return <div className="w-7 h-7 rounded-full bg-[#25D366] flex items-center justify-center"><MessageCircle className="w-3.5 h-3.5 text-white" /></div>;
      case 'message_sent': return <div className="w-7 h-7 rounded-full bg-[#002FA7] flex items-center justify-center"><Send className="w-3.5 h-3.5 text-white" /></div>;
      case 'message_received': return <div className="w-7 h-7 rounded-full bg-[#F59E0B] flex items-center justify-center"><MessageSquare className="w-3.5 h-3.5 text-white" /></div>;
      default: return <div className="w-7 h-7 rounded-full bg-[#71717A] flex items-center justify-center"><Bell className="w-3.5 h-3.5 text-white" /></div>;
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002FA7]"></div></div>;
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-[#71717A]" />
        <p className="text-[#71717A]">Ticket non trovato</p>
        <Button onClick={() => navigate('/tickets')} className="mt-4">Torna ai Ticket</Button>
      </div>
    );
  }

  const statusInfo = getStatusBadge(ticket.status);
  const messages = ticket.messages || [];
  const activityLog = ticket.activity_log || [];

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button variant="ghost" onClick={() => navigate('/tickets')} className="mb-2 -ml-2 text-[#71717A]" data-testid="back-to-tickets-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Torna ai Ticket
          </Button>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{ticket.title}</h1>
          <p className="text-sm text-[#71717A] mt-1 font-mono">ID: {ticket.id}</p>
        </div>
        {user?.role === 'admin' && (
          <Button variant="outline" className="text-[#DC2626] border-[#DC2626] hover:bg-red-50" onClick={handleDelete} data-testid="delete-ticket-btn">
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket Info */}
          <Card className="border border-[#E4E4E7] shadow-none" data-testid="ticket-info-card">
            <CardContent className="p-6">
              <div className="flex flex-wrap gap-3 mb-4">
                <Badge className={`${statusInfo.bg} border flex items-center gap-1`}>{statusInfo.icon}{ticket.status.replace('_', ' ')}</Badge>
                <Badge className={`${getPriorityBadge(ticket.priority)} border`}>Priorità: {ticket.priority}</Badge>
                <Badge variant="secondary" className="border">{ticket.category}</Badge>
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Descrizione</h3>
              <p className="text-[#0F172A] whitespace-pre-wrap">{ticket.description}</p>
              {(ticket.caller_name || ticket.caller_phone || ticket.caller_email) && (
                <div className="mt-6 pt-6 border-t border-[#E4E4E7]">
                  <h4 className="text-sm font-medium mb-3">Richiedente</h4>
                  <div className="flex flex-wrap gap-4">
                    {ticket.caller_name && <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-[#71717A]" />{ticket.caller_name}</div>}
                    {ticket.caller_email && <div className="flex items-center gap-2 text-sm"><span className="w-4 h-4 text-[#71717A]">@</span><a href={`mailto:${ticket.caller_email}`} className="text-[#002FA7] hover:underline">{ticket.caller_email}</a></div>}
                    {ticket.caller_phone && <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-[#71717A]" />{ticket.caller_phone}</div>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Diagnosis */}
          {ticket.ai_diagnosis && (
            <Card className="border-2 border-[#FDE047] shadow-[0_0_15px_rgba(253,224,71,0.2)]" data-testid="ai-diagnosis-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}><Bot className="w-5 h-5 text-[#D97706]" />Diagnosi REBECCA</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-[#FFFBEB] p-4 rounded-md border border-[#FDE047]">
                  <p className="text-sm whitespace-pre-wrap" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{ticket.ai_diagnosis}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Chat / Messages */}
          <Card className="border border-[#E4E4E7] shadow-none" data-testid="chat-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <MessageCircle className="w-5 h-5" />
                Conversazione con l'utente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Messages list */}
              <div className="bg-[#F4F4F5] rounded-lg p-4 mb-4 min-h-[120px] max-h-[400px] overflow-y-auto" data-testid="chat-messages">
                {messages.length === 0 ? (
                  <p className="text-sm text-[#71717A] text-center py-6">Nessun messaggio. Scrivi il primo messaggio all'utente.</p>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.sender === 'technician' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-lg p-3 ${
                          msg.sender === 'technician'
                            ? 'bg-[#002FA7] text-white'
                            : 'bg-white border border-[#E4E4E7]'
                        }`} data-testid={`chat-msg-${idx}`}>
                          <p className={`text-xs font-medium mb-1 ${msg.sender === 'technician' ? 'text-blue-200' : 'text-[#71717A]'}`}>
                            {msg.sender_name}
                            {msg.sent_via_email && <span className="opacity-70"> • via email</span>}
                            {msg.sent_via_whatsapp && <span className="opacity-70"> • via WhatsApp</span>}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                          <p className={`text-xs mt-1 ${msg.sender === 'technician' ? 'text-blue-300' : 'text-[#A1A1AA]'}`}>
                            {new Date(msg.timestamp).toLocaleString('it-IT')}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                )}
              </div>

              {/* Message input */}
              <div className="space-y-3">
                <Textarea
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  placeholder="Scrivi un messaggio all'utente..."
                  rows={3}
                  className="border-[#E4E4E7]"
                  data-testid="chat-input"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                {ticket.caller_email && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className="w-4 h-4 rounded border-[#E4E4E7] text-[#002FA7] focus:ring-[#002FA7]"
                      data-testid="send-email-checkbox"
                    />
                    <span className="text-sm text-[#0F172A]">
                      Invia anche via email a <strong>{ticket.caller_email}</strong>
                    </span>
                  </label>
                )}
                {ticket.caller_phone && (
                  <div className="flex items-center gap-2 text-sm text-[#25D366]" data-testid="whatsapp-auto-indicator">
                    <MessageCircle className="w-4 h-4" />
                    <span>WhatsApp automatico a <strong>{ticket.caller_phone}</strong></span>
                  </div>
                )}
                <Button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !chatMessage.trim()}
                  className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white"
                  data-testid="send-message-btn"
                >
                  {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Invia Messaggio
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* AI Chat */}
          <Card className="border border-[#E4E4E7] shadow-none" data-testid="ai-chat-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}><Bot className="w-5 h-5" />Chiedi a REBECCA</CardTitle>
            </CardHeader>
            <CardContent>
              {aiResponse && (
                <div className="mb-4 p-4 bg-[#F4F4F5] rounded-md border border-[#E4E4E7]">
                  <p className="text-sm font-medium text-[#002FA7] mb-2">Risposta IA:</p>
                  <p className="text-sm whitespace-pre-wrap">{aiResponse}</p>
                </div>
              )}
              <div className="flex gap-2">
                <Textarea value={aiMessage} onChange={(e) => setAiMessage(e.target.value)} placeholder="Chiedi a REBECCA..." rows={2} className="border-[#E4E4E7] flex-1" data-testid="ai-chat-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiChat(); } }}
                />
                <Button onClick={handleAiChat} className="bg-[#FDE047] text-[#0F172A] hover:bg-[#FACC15] self-end" disabled={aiLoading || !aiMessage.trim()} data-testid="send-ai-message-btn">
                  {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card className="border border-[#E4E4E7] shadow-none" data-testid="status-actions-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Azioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label>Cambia Stato</Label>
                <Select value={ticket.status} onValueChange={handleStatusChange} disabled={updating}>
                  <SelectTrigger data-testid="change-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Aperto</SelectItem>
                    <SelectItem value="in_progress">In Corso</SelectItem>
                    <SelectItem value="resolved">Risolto</SelectItem>
                    <SelectItem value="closed">Chiuso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" className="w-full" onClick={() => setShowAssignModal(true)} data-testid="reassign-ticket-btn">
                <User className="w-4 h-4 mr-2" />{ticket.assigned_to ? 'Riassegna' : 'Assegna'} Tecnico
              </Button>
            </CardContent>
          </Card>

          {/* Assignment */}
          <Card className="border border-[#E4E4E7] shadow-none" data-testid="assignment-info-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Assegnazione</CardTitle>
            </CardHeader>
            <CardContent>
              {ticket.assigned_technician_name ? (
                <div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#002FA7] text-white flex items-center justify-center font-medium">{ticket.assigned_technician_name.charAt(0)}</div>
                    <div>
                      <p className="font-medium">{ticket.assigned_technician_name}</p>
                      <p className="text-xs text-[#71717A]">Tecnico Assegnato</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#E4E4E7] space-y-2">
                    {ticket.assigned_technician_phone && (
                      <a href={buildWhatsAppMessage(ticket.assigned_technician_phone)} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-tech-btn">
                        <Button className="w-full bg-[#25D366] hover:bg-[#25D366]/90 text-white" type="button"><MessageCircle className="w-4 h-4 mr-2" />Invia su WhatsApp</Button>
                      </a>
                    )}
                    {ticket.assigned_technician_email && (
                      <a href={`mailto:${ticket.assigned_technician_email}?subject=${encodeURIComponent(`Ticket #${ticket.id?.slice(-8)}: ${ticket.title}`)}`} data-testid="email-tech-btn">
                        <Button variant="outline" className="w-full mt-2" type="button"><Mail className="w-4 h-4 mr-2" />Invia Email</Button>
                      </a>
                    )}
                    {!ticket.assigned_technician_phone && <p className="text-xs text-[#71717A] text-center">Nessun telefono registrato per WhatsApp</p>}
                  </div>
                </div>
              ) : (
                <p className="text-[#71717A] text-sm">Non assegnato</p>
              )}
            </CardContent>
          </Card>

          {/* Rich Timeline */}
          <Card className="border border-[#E4E4E7] shadow-none" data-testid="timeline-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activityLog.length > 0 ? (
                  [...activityLog].reverse().map((entry, idx) => (
                    <div key={idx} className="flex items-start gap-3" data-testid={`timeline-entry-${idx}`}>
                      {getActivityIcon(entry.action)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0F172A]">{entry.details}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-xs text-[#71717A]">{entry.user}</p>
                          <span className="text-xs text-[#A1A1AA]">•</span>
                          <p className="text-xs text-[#71717A] flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(entry.timestamp).toLocaleString('it-IT')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#002FA7] mt-2"></div>
                      <div>
                        <p className="text-sm font-medium">Ticket Creato</p>
                        <p className="text-xs text-[#71717A] flex items-center gap-1">
                          <Calendar className="w-3 h-3" />{new Date(ticket.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                    </div>
                    {ticket.updated_at !== ticket.created_at && (
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 rounded-full bg-[#D97706] mt-2"></div>
                        <div>
                          <p className="text-sm font-medium">Ultimo Aggiornamento</p>
                          <p className="text-xs text-[#71717A] flex items-center gap-1">
                            <Calendar className="w-3 h-3" />{new Date(ticket.updated_at).toLocaleString('it-IT')}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assign Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Assegna Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#71717A]">Seleziona un tecnico per assegnare questo ticket</p>
            <ScrollArea className="h-[300px]">
              <div className="space-y-2 pr-4">
                {technicians.map((tech) => (
                  <Button key={tech.id} variant="outline"
                    className={`w-full justify-start h-auto p-3 ${tech.id === ticket.assigned_to ? 'border-[#002FA7] bg-blue-50' : ''}`}
                    onClick={() => handleAssign(tech.id)} data-testid={`assign-modal-${tech.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-[#E4E4E7] flex items-center justify-center font-medium">{tech.name.charAt(0)}</div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                          tech.status === 'available' ? 'bg-[#16A34A]' : tech.status === 'busy' ? 'bg-[#D97706]' : 'bg-[#71717A]'
                        }`}></div>
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{tech.name}</p>
                        <p className="text-xs text-[#71717A]">{tech.specialty} - {tech.tickets_assigned} ticket</p>
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
